import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { Condition, Encounter, EpisodeOfCare } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import type * as fhirTypes from "fhir/r4";

export interface RawVisitDiagnosesResponse {
  motherEncounter: fhirTypes.Encounter;
  episodeOfCareResources: {
    childEncounter: fhirTypes.Encounter | null;
    episodeOfCare: fhirTypes.EpisodeOfCare;
    condition: fhirTypes.Condition;
  }[];
  visitDiagnoses: fhirTypes.Condition[];
  billingDiagnoses: fhirTypes.Condition[];
}

export interface VisitDiagnosesResponse {
  motherEncounter: Encounter;
  episodeOfCareResources: {
    childEncounter: Encounter | null;
    episodeOfCare: EpisodeOfCare;
    condition: Condition;
  }[];
  visitDiagnoses: Condition[];
  billingDiagnoses: Condition[];
}

export const selectVisitDiagnoses = (
  response: RawVisitDiagnosesResponse,
): VisitDiagnosesResponse => ({
  motherEncounter: new Encounter(response.motherEncounter),
  episodeOfCareResources: (response.episodeOfCareResources ?? []).map(
    (resources) => ({
      childEncounter: resources.childEncounter
        ? new Encounter(resources.childEncounter)
        : null,
      episodeOfCare: new EpisodeOfCare(resources.episodeOfCare),
      condition: new Condition(resources.condition),
    }),
  ),
  visitDiagnoses: (response.visitDiagnoses ?? []).map(
    (condition) => new Condition(condition),
  ),
  billingDiagnoses: (response.billingDiagnoses ?? []).map(
    (condition) => new Condition(condition),
  ),
});

export const useVisitDiagnoses = (encounterId?: string, enabled = true) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  return useApi<RawVisitDiagnosesResponse, VisitDiagnosesResponse>({
    queryOptions: {
      enabled: Boolean(encounterId) && enabled,
      queryFn: async () => {
        const response = await axiosInstance.get<RawVisitDiagnosesResponse>(
          "/charting/encounter-charting/resources",
          {
            params: {
              motherEncounterId: encounterId,
            },
          },
        );

        return response.data;
      },
      select: selectVisitDiagnoses,
      queryKey: QueryKeys.Patient.Chart.Conditions.VisitDiagnoses({
        patientId,
        encounterId,
      }),
    },
  });
};
